import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, Clock, MessageSquare, Check } from 'lucide-react';
import { Button } from './ui/button';

const RefinementSection = ({ 
  onRefine, 
  onContinue, 
  onSkip,
  isRefining = false,
  refinedData = null,
  stepName = "vision"
}) => {
  const [notes, setNotes] = useState('');
  const [showRefined, setShowRefined] = useState(false);

  const handleRefine = async () => {
    if (!notes.trim()) return;
    await onRefine(notes);
    setShowRefined(true);
  };

  const placeholderExamples = {
    profile: "Ex: Je suis plus audacieuse que ça, j'aime les couleurs vives et l'énergie...",
    vision: "Ex: J'aime le luxe mais pas le minimalisme, je veux quelque chose d'authentique...",
    content: "Ex: Mon ton est plus chaleureux, je parle comme une amie proche..."
  };

  // Show refined result screen
  if (showRefined && refinedData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl mx-auto px-6 py-12"
      >
        {/* Success indicator */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="w-16 h-16 mx-auto mb-6 bg-[#D4AF37]/20 border border-[#D4AF37]/40 rounded-full flex items-center justify-center"
        >
          <Check className="text-[#D4AF37]" size={28} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase text-center mb-4"
        >
          Vision affinée
        </motion.p>

        <motion.h3
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-white text-2xl md:text-3xl text-center mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Ta vision affinée
        </motion.h3>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="text-[#71717A] text-sm text-center mb-8"
        >
          C'est maintenant ta vraie direction.
        </motion.p>

        {/* Refinement applied badge */}
        {refinedData.refinement_applied && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-4 mb-8"
          >
            <p className="text-[#D4AF37]/80 text-xs uppercase tracking-wider mb-2">
              Ajustements appliqués
            </p>
            <p className="text-[#A1A1AA] text-sm">
              {refinedData.refinement_applied}
            </p>
          </motion.div>
        )}

        {/* Continue button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="flex justify-center"
        >
          <Button
            onClick={onContinue}
            className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-10 py-6 text-base font-medium"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="continue-after-refine"
          >
            Continuer <ArrowRight className="ml-3" size={18} />
          </Button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full max-w-2xl mx-auto px-6 py-8"
    >
      {/* Decorative line */}
      <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent mx-auto mb-8" />

      {/* Title */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="text-center mb-6"
      >
        <div className="w-12 h-12 mx-auto mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
          <MessageSquare className="text-[#D4AF37]" size={20} />
        </div>
        
        <h3 
          className="text-white text-xl md:text-2xl mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Est-ce que ça te ressemble vraiment ?
        </h3>
        
        <p className="text-[#71717A] text-sm">
          Tu peux affiner cette vision avec tes propres mots.
          <br />
          C'est ici qu'elle devient vraiment alignée.
        </p>
      </motion.div>

      {/* Input area */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="mb-6"
      >
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={placeholderExamples[stepName] || placeholderExamples.vision}
          className="w-full h-32 bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 text-white text-sm placeholder-[#52525B] focus:border-[#D4AF37]/50 focus:outline-none resize-none transition-colors"
          data-testid="refinement-input"
        />
        <p className="text-[#52525B] text-xs mt-2 text-right">
          {notes.length} / 500 caractères
        </p>
      </motion.div>

      {/* Action buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="flex flex-col items-center gap-3"
      >
        {/* Primary: Refine */}
        <Button
          onClick={handleRefine}
          disabled={!notes.trim() || isRefining}
          className={`w-full max-w-sm py-6 text-base font-medium ${
            notes.trim() 
              ? 'bg-[#D4AF37] hover:bg-[#B8860B] text-black' 
              : 'bg-[#27272A] text-[#52525B] cursor-not-allowed'
          }`}
          style={{ fontFamily: "'Playfair Display', serif" }}
          data-testid="refine-vision-btn"
        >
          {isRefining ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="mr-2"
              >
                <Sparkles size={18} />
              </motion.div>
              Affinage en cours...
            </>
          ) : (
            <>
              <Sparkles className="mr-2" size={18} />
              Affiner ma vision
            </>
          )}
        </Button>

        {/* Secondary: Continue */}
        <Button
          onClick={onContinue}
          disabled={isRefining}
          className="w-full max-w-sm py-5 bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
          data-testid="continue-without-refine"
        >
          Continuer <ArrowRight className="ml-2" size={16} />
        </Button>

        {/* Tertiary: Skip */}
        <button
          onClick={onSkip}
          disabled={isRefining}
          className="text-[#52525B] text-sm hover:text-[#71717A] transition-colors flex items-center gap-2 py-2"
          data-testid="skip-refinement"
        >
          <Clock size={14} />
          Je le ferai plus tard
        </button>
      </motion.div>
    </motion.div>
  );
};

export default RefinementSection;
